import { html, css, LitElement } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { when } from 'lit/directives/when.js';
import { ShouldUpdate } from '../../modules/ShouldUpdate.js';
import { GetFlagDefinitionsForEntity } from '../../modules/GetCustomizationDefinitions.js';
import '@shoelace-style/shoelace/dist/components/switch/switch.js';
import '@shoelace-style/shoelace/dist/components/tag/tag.js';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';
import posthog from 'posthog-js';

/**
 * @import { FeatureFlagType } from '../../app-configuration/customization/feature-flag-definitions.js';;
 */

export class FeatureFlagsList extends LitElement {
  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: var(--sl-spacing-small);
    }
    sl-switch::part(label) {
      display: flex;
      gap: var(--sl-spacing-x-small);
      align-items: center;
    }
    sl-switch::part(form-control-help-text) {
      display: flex;
      flex-direction: column;
      gap: var(--sl-spacing-2x-small);
      margin-left: 44px;
    }
    sl-tag::part(content) {
      display: flex;
      align-items: center;
      gap: var(--sl-spacing-2x-small);
    }
  `;

  static properties = {
    entityType: { type: String, shouldUpdate: false },
    entityId: { type: String, shouldUpdate: false },
    showAllFlags: { type: Boolean },
    _entityTypeFeatureFlagsRef: { type: Object, shouldUpdate: false },
    _serverFeatureFlagsRef: { type: Object, shouldUpdate: false },
    _flagToggleErrorMessages: { type: Object },
    _entityTypeFeatureFlagDefinitions: { type: Array },
    _entityTypeFeatureFlagValues: { type: Object },
    _serverFeatureFlagValues: { type: Object }
  };

  get entityTypeFeatureFlagDefinitions() {
    return this._entityTypeFeatureFlagDefinitions;
  }

  render() {
    const { _entityTypeFeatureFlagDefinitions, _entityTypeFeatureFlagValues } = this;

    return html`
      ${repeat(
        _entityTypeFeatureFlagDefinitions,
        (flag) => flag.name,
        (flag) => html`
          <sl-tooltip
            content=${
              flag.restricted_by_server_flag
                ? 'This experimental feature can only be enabled or disabled by the server owner.'
                : 'Please contact support to enable or disable this experimental feature!'
            }
            placement="top-start"
            ?disabled=${!flag.restricted && !flag.restricted_by_server_flag}
            hoist
          >
            <sl-switch
              .checked=${_entityTypeFeatureFlagValues?.[flag.name] ?? false}
              ?disabled=${flag.restricted || flag.restricted_by_server_flag}
              @sl-change=${(e) => this.#flagToggled(e, flag)}
              class="ph-no-capture"
            >
              <span>${flag.label}</span>
              ${when(
                flag.private,
                () =>
                  html`<sl-tag variant="neutral" size="small" pill>
                    <katapult-icon icon="visibility_lock" size="16"></katapult-icon>
                    <span>Private</span>
                  </sl-tag> `
              )}
              ${when(flag.description != null, () => html`<span slot="help-text">${flag.description}</span>`)}
              ${when(
                this._flagToggleErrorMessages?.[flag.name] != null,
                () => html`
                  <div
                    style="display: flex; align-items: center; gap: var(--sl-spacing-x-small); color: var(--sl-color-danger);"
                    slot="help-text"
                  >
                    <katapult-icon icon="error" size="20"></katapult-icon>
                    <span>${this._flagToggleErrorMessages[flag.name]}</span>
                  </div>
                `
              )}
            </sl-switch>
          </sl-tooltip>
        `
      )}
    `;
  }

  constructor() {
    super();

    /** @type {?FeatureFlagType} */
    this.entityType = null;

    /** @type {?string} */
    this.entityId = null;

    /** @type {boolean} */
    this.showAllFlags = false;

    /** @type {Object} */
    this._entityTypeFeatureFlagValues = {};

    /** @type {Object} */
    this._serverFeatureFlagValues = {};

    /** @type {Object} */
    this._flagToggleErrorMessages = {};

    /** @type {Array} */
    this._entityTypeFeatureFlagDefinitions = [];
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._disconnectEntityTypeFeatureFlagsListener?.();
  }

  shouldUpdate(changedProperties) {
    if (['entityType', 'entityId'].some((prop) => changedProperties.has(prop))) {
      this.#updateEntityTypeFeatureFlagsRef();
    }

    if (changedProperties.has('_entityTypeFeatureFlagsRef')) {
      this.#updateEntityTypeFeatureFlagsListener();
    }

    if (['_entityTypeFeatureFlagValues', '_serverFeatureFlagValues'].some((prop) => changedProperties.has(prop))) {
      this.#updateFeatureFlagDefinitions();
    }

    if (changedProperties.has('_entityTypeFeatureFlagDefinitions')) {
      this.dispatchEvent(new CustomEvent('entity-type-feature-flag-definitions-changed'));
    }

    return ShouldUpdate(this, changedProperties);
  }

  #updateEntityTypeFeatureFlagsRef() {
    const FEATURE_FLAGS_REF = globalThis.FirebaseWorker.ref(`photoheight/feature_flags`);

    const { entityType, entityId } = this;
    if (!entityType || (!entityId && (entityType == 'user' || entityType == 'company'))) return;

    this._serverFeatureFlagsRef = null;

    switch (entityType) {
      case 'user': {
        this._entityTypeFeatureFlagsRef = FEATURE_FLAGS_REF.child(`users/${entityId}`);
        this._serverFeatureFlagsRef = FEATURE_FLAGS_REF.child(`server`);
        break;
      }
      case 'company': {
        this._entityTypeFeatureFlagsRef = FEATURE_FLAGS_REF.child(`companies/${entityId}`);
        this._serverFeatureFlagsRef = FEATURE_FLAGS_REF.child(`server`);
        break;
      }
      case 'server': {
        this._entityTypeFeatureFlagsRef = FEATURE_FLAGS_REF.child(`server`);
        break;
      }
      default: {
        this._entityTypeFeatureFlagsRef = null;
      }
    }
  }

  #updateEntityTypeFeatureFlagsListener() {
    this._disconnectEntityTypeFeatureFlagsListener?.();

    const { _entityTypeFeatureFlagsRef } = this;
    if (!_entityTypeFeatureFlagsRef) return;

    const entityTypeCallback = (snapshot) => (this._entityTypeFeatureFlagValues = snapshot.val() ?? {});
    _entityTypeFeatureFlagsRef.on('value', entityTypeCallback);
    const serverCallback = (snapshot) => (this._serverFeatureFlagValues = snapshot.val() ?? {});
    this._serverFeatureFlagsRef?.on('value', serverCallback);

    this._disconnectEntityTypeFeatureFlagsListener = () => {
      _entityTypeFeatureFlagsRef.off('value', entityTypeCallback);
      this._serverFeatureFlagsRef?.off('value', serverCallback);
      this._entityTypeFeatureFlagValues = {};
      this._serverFeatureFlagValues = {};
      this._disconnectEntityTypeFeatureFlagsListener = null;
    };
  }

  #updateFeatureFlagDefinitions() {
    const { entityType, _entityTypeFeatureFlagValues, _serverFeatureFlagValues, showAllFlags } = this;
    if (!entityType) {
      this._entityTypeFeatureFlagDefinitions = [];
      return;
    }

    const entityTypeFeatureFlagNames = Object.keys(_entityTypeFeatureFlagValues ?? {});
    const filteredEntityTypeFeatureFlagNames = entityTypeFeatureFlagNames.filter((key) => !key.startsWith('_'));
    const entityTypeFeatureFlagDefinitions = GetFlagDefinitionsForEntity(entityType, filteredEntityTypeFeatureFlagNames, {
      includeAllPrivate: showAllFlags
    });
    const sortedFeatureFlagDefinitions = entityTypeFeatureFlagDefinitions.sort((a, b) => a.label.localeCompare(b.label));

    // If desired, get the unknown feature flag definitions
    const unknownFeatureFlagNames = showAllFlags
      ? entityTypeFeatureFlagNames.filter((flagName) => !sortedFeatureFlagDefinitions.some((flag) => flag.name === flagName))
      : [];
    const unknownFeatureFlagDefinitions = unknownFeatureFlagNames.map((flagName) => ({
      name: flagName,
      type: entityType,
      label: `(Unknown) ${flagName}`
    }));
    const sortedUnknownFeatureFlagDefinitions = unknownFeatureFlagDefinitions.sort((a, b) => a.label.localeCompare(b.label));

    const definitions = [...sortedFeatureFlagDefinitions, ...sortedUnknownFeatureFlagDefinitions];

    // Check if any of the feature flags for the entity should be linked with server feature flags
    if (entityType !== 'server') {
      definitions.forEach((definition) => {
        const { name } = definition;
        const matchingServerFlag = Object.keys(_serverFeatureFlagValues ?? {}).find((serverFlagName) => serverFlagName === name);
        if (!matchingServerFlag) return;

        const serverFlagValue = _serverFeatureFlagValues[matchingServerFlag];
        definition.restricted_by_server_flag = true;
        _entityTypeFeatureFlagValues[name] = serverFlagValue;
      });
    }

    // Sort the feature flag definitions by name, known first, then unknown
    this._entityTypeFeatureFlagDefinitions = definitions;
  }

  async #flagToggled(e, flag) {
    // get the data needed to perform the operation
    const switchElement = e.target;
    const flagName = flag.name;
    const state = switchElement.checked;
    const { entityType, entityId } = this;
    const entityIdIsMissing = entityId == null && (entityType == 'user' || entityType == 'company');
    if ([flagName, state, entityType].some((dataEntry) => dataEntry == null) || entityIdIsMissing) return;

    // disable the switch so the user can't toggle it while the database is updating
    switchElement.disabled = true;

    // set the feature flag in the database
    try {
      // call the firebase function to set the feature flag
      const functionData = { flagName, entityType, entityId, state };
      await firebase.functions().httpsCallable('updateFeatureFlagStates')(functionData);

      // remove any error messages for the flag
      delete this._flagToggleErrorMessages[flagName];

      // Track the feature flags toggled
      posthog.capture('toggled_experimental_feature', { value: state, flag_name: flagName, entity_type: entityType, entity_id: entityId });

      // enable the switch again
      switchElement.disabled = false;
    } catch (e) {
      // reset the flag's state to what is in the database
      const currentDatabaseStateForFlag = this._entityTypeFeatureFlagValues[flagName];
      switchElement.checked = currentDatabaseStateForFlag;

      // show an error message to the user for the flag
      this._flagToggleErrorMessages[flagName] = e.message ?? 'An error occurred while updating the feature flag.';

      // enable the switch again after its state is reset
      switchElement.disabled = false;
    }
    this._flagToggleErrorMessages = { ...this._flagToggleErrorMessages };
  }
}

customElements.define('feature-flags-list', FeatureFlagsList);
